import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import {
  CREATE_UNSUPPORTED_POPUP,
  DELETE_UNSUPPORTED_POPUP,
  GET_UNSUPPORTED_POPUPS,
  UPDATE_UNSUPPORTED_POPUP,
} from './graphql';
import { normalizeVersion } from '../VersionControl/utils';

type LocaleTextMap = Record<string, string>;
type PopupTargetSystemName = 'IOS' | 'ANDROID';
type PopupType = 'REMINDER' | 'BLOCKER';
type PopupDisplayPolicy = 'EVERY_LAUNCH' | 'ONCE_PER_DAY';

type PopupConfig = {
  id: string;
  key: string;
  name: string;
  enabled: boolean;
  place: 'MAIN_MENU';
  targetSystemName: PopupTargetSystemName;
  popupType: PopupType;
  minimumDeviceVersion?: string | null;
  blockerReleaseVersion?: string | null;
  displayPolicy: PopupDisplayPolicy;
  titleTexts?: LocaleTextMap | null;
  bodyTexts?: LocaleTextMap | null;
};

const DEFAULT_TITLE_TEXTS: LocaleTextMap = {
  en: '{devicePlatform} Version\nUpdate Required',
  fr: 'Version {devicePlatform}\nMise à jour requise',
  de: '{devicePlatform} Version\nUpdate erforderlich',
  it: 'Versione {devicePlatform}\nAggiornamento richiesto',
  es: 'Actualización necesaria de\nversión de {devicePlatform}',
  pt: 'Versão de {devicePlatform}\nAtualização necessária',
  id: 'Memerlukan Update\nVersi {devicePlatform}',
  tr: '{devicePlatform} Sürümü\nGüncelleme Gerekli',
  pl: 'Wersja {devicePlatform}\nWymagana aktualizacja',
  nl: '{devicePlatform} Versie\nUpdate vereist',
  sv: '{devicePlatform}-version\nUppdatering krävs',
  da: '{devicePlatform}-version\nOpdatering påkrævet',
  no: '{devicePlatform}-versjon\nOppdatering kreves',
  fi: '{devicePlatform}-versio\nPäivitys vaadittu',
  el: '?????????? ????????? ????\n?????? {devicePlatform}',
  cs: 'Verze platformy {devicePlatform}\nJe nutná aktualizace',
};
const DEFAULT_BODY_TEXTS: LocaleTextMap = {
  en: 'We sincerely apologize for any inconvenience. To ensure a smooth experience and maintain the highest standards of stability and security, devices running below {version} will no longer be supported in the upcoming update.\n\nPlease update your device to {version} or later to continue your musical journey without interruption.\n\nRest assured, your game progress and account data will remain completely safe.',
  fr: 'Nous vous présentons nos plus sincères excuses pour la gêne occasionnée. Pour garantir une expérience fluide et conserver des standards élevés en matière de stabilité et de sécurité, les appareils fonctionnant sous une version inférieure à la version {version} ne seront plus pris en charge dans la prochaine mise à jour.\n\nVeuillez installer la version {version} ou une version ultérieure sur votre appareil afin de poursuivre votre aventure musicale sans interruption.\n\nNous vous assurons que votre progression dans le jeu et les données de votre compte resteront parfaitement en sécurité.',
  de: 'Wir entschuldigen uns aufrichtig für etwaige Unannehmlichkeiten. Um einen reibungslosen Ablauf zu gewährleisten und höchste Standards in Bezug auf Stabilität und Sicherheit zu gewährleisten, werden Geräte mit einer Version unter {version} im kommenden Update nicht mehr unterstützt.\n\nBitte aktualisiere dein Gerät auf {version} oder höher, um deine musikalische Reise ohne Unterbrechung fortzusetzen.\n\nSei versichert, dass dein Spielfortschritt und deine Kontodaten vollkommen sicher bleiben.',
  it: 'Ci scusiamo per l\'inconveniente. Per garantire un\'esperienza fluida e mantenere i più alti standard di stabilità e sicurezza, i dispositivi con versione precedente a {version} non saranno più supportati nel prossimo aggiornamento.\n\nAggiorna il tuo dispositivo alla versione {version} o successiva per continuare il tuo viaggio musicale senza interruzioni.\n\nI progressi di gioco e i dati del tuo account rimarranno completamente al sicuro.',
  es: 'Te pedimos disculpas por cualquier molestia. Para garantizar una experiencia fluida y mantener los más altos estándares de estabilidad y seguridad, los dispositivos con versiones anteriores a {version} ya no serán compatibles con la próxima actualización.\n\nActualiza tu dispositivo a la versión {version} o posterior para continuar tu viaje musical sin interrupciones.\n\nTu progreso en el juego y los datos de tu cuenta permanecerán completamente seguros.',
  pt: 'Pedimos sinceras desculpas por qualquer inconveniente. Para garantir uma experiência suave e manter os mais altos padrões de estabilidade e segurança, os aparelhos com versões anteriores à {version} não serão mais compatíveis na próxima atualização.\n\nAtualize seu aparelho para {version} ou mais recente para continuar sua jornada musical sem interrupções.\n\nNão se preocupe. Seu progresso no jogo e os dados da sua conta permanecerão completamente seguros.',
  id: 'Mohon maaf atas ketidaknyamanan yang terjadi. Untuk memastikan pengalaman yang mulus dan mempertahankan standar stabilitas dan keamanan tertinggi, perangkat yang beroperasi dengan versi lebih lama dari {version} tidak akan didukung lagi di update berikutnya.\n\nHarap mengupdate perangkatmu ke versi {version} atau lebih baru untuk melanjutkan perjalanan musikmu tanpa gangguan.\n\nJangan khawatir, progres game dan data akunmu akan tetap aman.',
  tr: 'Bu durumunun meydana getirebilece?i tüm aksakl?klardan dolay? özür dileriz. Sorunsuz bir deneyim sa?lamak, en yüksek istikrar ve güvenlik standartlar?n? korumak için {version} alt?ndaki sürümlerde çal??an cihazlar yakla?an güncellemede art?k desteklenmeyecek.\n\nMüzik yolculu?una kesintisiz devam etmek istiyorsan lütfen cihaz?n? {version} veya daha yeni bir sürüme güncelle.\n\nOyun ilerlemenin ve hesap verilerinin tamamen güvende kalaca??ndan emin olabilirsin.',
  pl: 'Serdecznie przepraszamy za wszelkie niedogodno?ci. Aby zapewni? p?ynne funkcjonowanie i najwy?sze standardy stabilno?ci, urz?dzenia dzia?aj?ce poni?ej wersji {version} nie b?d? ju? obs?ugiwane w nast?pnej aktualizacji.\n\nProsimy o dokonanie aktualizacji urz?dzenia do wersji {version} lub pó?niejszej, aby móc bez przeszkód kontynuowa? swoj? muzyczn? podró?.\n\nGwarantujemy, ?e post?py w grze i dane konta pozostan? ca?kowicie bezpieczne.',
  nl: 'Onze excuses voor het ongemak. Om een soepele ervaring te garanderen en de hoogste normen op het gebied van stabiliteit en veiligheid te handhaven, worden apparaten met een versie lager dan {version} niet langer ondersteund in de komende update.\n\nWerk je apparaat bij naar {version} of hoger om je muzikale reis zonder onderbrekingen voort te zetten.\n\nMaak je geen zorgen: je voortgang in het spel en je accountgegevens blijven volledig veilig.',
  sv: 'Vi beklagar verkligen för alla eventuella besvär. För att kunna säkerställa en välfungerande upplevelse och upprätthålla högsta stabilitets- och säkerhetsstandard kommer enheter som använder versioner lägre än {version} inte längre att stödjas i den kommande uppdateringen.\n\nUppdatera din enhet till {version} eller senare för att fortsätta din musikresa utan störningar.\n\nVi försäkrar att dina framsteg i spelet och dina kontouppgifter förblir helt säkra.',
  da: 'Vi beklager ulejligheden. For at sikre en problemfri oplevelse og de højeste standarder for stabilitet og sikkerhed vil enheder, der kører en version under {version}, ikke længere være understøttet med den næste opdatering.\n\nOpdater din enhed til {version} eller nyere for at fortsætte din musikalske rejse uden afbrydelser.\n\nDu kan være sikker på, at din fremgang i spillet og dine kontooplysninger forbliver fuldstændig sikre.',
  no: 'Vi beklager på det sterkeste eventuelle ulemper. For å tilby en problemfri opplevelse og opprettholde de høyeste standarder for stabilitet og sikkerhet, vil ikke lenger enheter som kjører versjoner eldre enn {version} støttes i den kommende oppdateringen.\n\nVennligst oppdater enheten din til {version} eller nyere for å fortsette din musikalske reise uten avbrytelser.\n\nDu kan være trygg på at spillfremdriften og kontoinformasjonen din oppbevares trygt.',
  fi: 'Pahoittelemme vilpittömästi aiheuttamaamme vaivaa. Varmistaaksemme sulavan käyttökokemuksen ja ylläpitääksemme korkeimpia vakauden ja turvallisuuden standardeja tuleva päivityksemme ei enää tue versiota {version} vanhempia laitteita.\n\nPäivitä laitteesi vähintään versioon {version} jatkaaksesi sulavaa musiikkimatkaasi.\n\nEi huolta, peliedistymisesi ja tilitietosi pysyvät täysin turvassa.',
  el: '??????? ????????? ???????? ??? ??? ????? ??????????.\n??? ?? ???????????? ??? ????? ???????? ??? ?? ??????????? ?? ????????? ??????? ???????????? ??? ?????????, ?? ???????? ??? ??????????? ?? ?????? ?????????? ??? ??? {version} ??? ?? ?????????????? ????? ???? ?????????? ?????????.\n\n?????????? ?? ??????? ??? ???? ?????? {version} ? ??????? ??? ?? ?????????? ?? ??????? ?????? ??? ????? ???????.\n\n??? ?????????????? ??? ? ??????? ??? ?????????? ??? ??? ?? ???????? ??? ??????????? ??? ?? ??????????? ???????? ??????.',
  cs: 'Up?ímn? se omlouváme za nep?íjemnosti. Kv?li zajit?ní hladké hry a zachování nejvyích standard? stability a zabezpe?ení u v p?ítí aktualizaci nebudou podporována za?ízení pouívající verzi starí ne {version}.\n\nAktualizuj za?ízení na verzi {version} nebo nov?jí a pokra?uj v hudební cest? bez p?eruení.\n\nTv?j postup ve h?e a údaje ú?tu budou i nadále naprosto v bezpe?í.',
};
const formatJson = (value: LocaleTextMap | null | undefined) =>
  JSON.stringify(value ?? { en: '' }, null, 2);

const parseLocaleTextMap = (value: string, fieldName: string): LocaleTextMap => {
  let parsedValue: unknown;

  try {
    parsedValue = JSON.parse(value);
  } catch {
    throw new Error(`${fieldName} must be valid JSON.`);
  }

  if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
    throw new Error(`${fieldName} must be a JSON object like {"en":"..."}.`);
  }

  const entries = Object.entries(parsedValue).filter(
    ([locale, text]) => typeof locale === 'string' && typeof text === 'string'
  );

  if (!entries.length) {
    throw new Error(`${fieldName} must contain at least one locale entry.`);
  }

  return Object.fromEntries(entries);
};

const readJsonFile = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => {
      const result = event.target?.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file.'));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsText(file);
  });

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const createDraftPopup = (
  platform: PopupTargetSystemName = 'IOS',
  popupType: PopupType = 'REMINDER'
): PopupConfig => {
  const name = `${platform === 'IOS' ? 'iOS' : 'Android'} ${popupType === 'REMINDER' ? 'Reminder' : 'Blocker'}`;
  const slug = slugify(name) || 'unsupported_popup';

  return {
    id: `unsupported-${slug}-${Date.now()}`,
    key: `unsupported_${slug}`,
    name,
    enabled: true,
    place: 'MAIN_MENU',
    targetSystemName: platform,
    popupType,
    minimumDeviceVersion: platform === 'IOS' ? '15.1' : '10.0',
    blockerReleaseVersion: '003.013.000',
    displayPolicy: popupType === 'BLOCKER' ? 'EVERY_LAUNCH' : 'ONCE_PER_DAY',
    titleTexts: DEFAULT_TITLE_TEXTS,
    bodyTexts: DEFAULT_BODY_TEXTS,
  };
};

const getPopupIdentity = (platform: PopupTargetSystemName, popupType: PopupType) => {
  const name = `${platform === 'IOS' ? 'iOS' : 'Android'} ${popupType === 'REMINDER' ? 'Reminder' : 'Blocker'}`;
  const slug = slugify(name) || 'unsupported_popup';

  return {
    name,
    key: `unsupported_${slug}`,
  };
};

const resolvePreviewLocaleText = (texts: LocaleTextMap | null | undefined, fallbackText: string) => {
  if (!texts || typeof texts !== 'object') {
    return fallbackText;
  }

  const candidates = ['en', 'en-US', 'en_GB'];
  for (const candidate of candidates) {
    const value = texts[candidate];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }

  const firstValue = Object.values(texts).find((value) => typeof value === 'string' && value.trim().length > 0);
  return typeof firstValue === 'string' ? firstValue : fallbackText;
};

const applyPopupTemplateValues = (
  text: string,
  platform: PopupTargetSystemName,
  minimumDeviceVersion: string | null | undefined
) => {
  const platformLabel = platform === 'ANDROID' ? 'Android' : 'iOS';
  const version = minimumDeviceVersion?.trim() || '15.1';

  return text
    .replace(/\b(iOS|Android)\b/g, platformLabel)
    .replace(/\{devicePlatform\}/g, platformLabel)
    .replace(/\{version\}/g, `${platformLabel} ${version}`)
    .replace(/\{minimumDeviceVersion\}/g, version);
};

const splitByHighlightTokens = (text: string, tokens: string[]) => {
  const validTokens = tokens.filter(Boolean).sort((left, right) => right.length - left.length);
  if (!validTokens.length) {
    return [{ type: 'text', value: text }];
  }

  const escapedTokens = validTokens.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escapedTokens.join('|')})`, 'g');

  return text
    .split(pattern)
    .filter((part) => part.length > 0)
    .map((part) => ({
      type: validTokens.includes(part) ? 'highlight' : 'text',
      value: part,
    }));
};

const UnsupportedPopup = () => {
  const [selectedPopupId, setSelectedPopupId] = useState<string | null>(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusVariant, setStatusVariant] = useState<'success' | 'danger' | 'info'>('info');
  const [isDraft, setIsDraft] = useState(false);
  const [id, setId] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [targetSystemName, setTargetSystemName] = useState<PopupTargetSystemName>('IOS');
  const [popupType, setPopupType] = useState<PopupType>('REMINDER');
  const [minimumDeviceVersion, setMinimumDeviceVersion] = useState('15.1');
  const [blockerReleaseVersion, setBlockerReleaseVersion] = useState('003.013.000');
  const [displayPolicy, setDisplayPolicy] = useState<PopupDisplayPolicy>('ONCE_PER_DAY');
  const [titleTextsJson, setTitleTextsJson] = useState(formatJson(DEFAULT_TITLE_TEXTS));
  const [bodyTextsJson, setBodyTextsJson] = useState(formatJson(DEFAULT_BODY_TEXTS));

  const { data, loading, refetch } = useQuery(GET_UNSUPPORTED_POPUPS, {
    fetchPolicy: 'network-only',
  });
  const [createPopupConfig, { loading: createLoading }] = useMutation(CREATE_UNSUPPORTED_POPUP);
  const [updatePopupConfig, { loading: updateLoading }] = useMutation(UPDATE_UNSUPPORTED_POPUP);
  const [deletePopupConfig, { loading: deleteLoading }] = useMutation(DELETE_UNSUPPORTED_POPUP);

  const unsupportedPopups: PopupConfig[] = useMemo(() => {
    const configs = data?.liveOps?.unsupportedPopupConfigs ?? [];
    return [...configs].sort((left: PopupConfig, right: PopupConfig) => left.name.localeCompare(right.name));
  }, [data?.liveOps?.unsupportedPopupConfigs]);

  const selectedPopup = useMemo(
    () => unsupportedPopups.find((popup) => popup.id === selectedPopupId) ?? null,
    [selectedPopupId, unsupportedPopups]
  );

  useEffect(() => {
    if (isDraft) {
      return;
    }

    if (!selectedPopupId && unsupportedPopups.length > 0) {
      setSelectedPopupId(unsupportedPopups[0].id);
      return;
    }

    if (selectedPopupId && !selectedPopup) {
      setSelectedPopupId(unsupportedPopups[0]?.id ?? null);
    }
  }, [isDraft, selectedPopup, selectedPopupId, unsupportedPopups]);

  useEffect(() => {
    if (!selectedPopup) {
      return;
    }

    setId(selectedPopup.id);
    setEnabled(selectedPopup.enabled);
    setTargetSystemName(selectedPopup.targetSystemName);
    setPopupType(selectedPopup.popupType);
    setMinimumDeviceVersion(selectedPopup.minimumDeviceVersion ?? '');
    setBlockerReleaseVersion(selectedPopup.blockerReleaseVersion ?? '');
    setDisplayPolicy(selectedPopup.displayPolicy);
    setTitleTextsJson(formatJson(selectedPopup.titleTexts));
    setBodyTextsJson(formatJson(selectedPopup.bodyTexts));
    setIsDraft(false);
  }, [selectedPopup]);

  useEffect(() => {
    if (popupType === 'BLOCKER' && displayPolicy !== 'EVERY_LAUNCH') {
      setDisplayPolicy('EVERY_LAUNCH');
    }
  }, [displayPolicy, popupType]);

  const parsedTitleTexts = useMemo(() => {
    try {
      return parseLocaleTextMap(titleTextsJson, 'Title LOC');
    } catch {
      return null;
    }
  }, [titleTextsJson]);

  const parsedBodyTexts = useMemo(() => {
    try {
      return parseLocaleTextMap(bodyTextsJson, 'Description LOC');
    } catch {
      return null;
    }
  }, [bodyTextsJson]);

  const previewPlatformLabel = targetSystemName === 'ANDROID' ? 'Android' : 'iOS';
  const previewVersion = minimumDeviceVersion.trim() || '15.1';
  const previewHighlightTokens = useMemo(
    () => [`${previewPlatformLabel} ${previewVersion}`, previewPlatformLabel, previewVersion],
    [previewPlatformLabel, previewVersion]
  );

  const editorPreviewTitle = useMemo(
    () =>
      applyPopupTemplateValues(
        resolvePreviewLocaleText(parsedTitleTexts, DEFAULT_TITLE_TEXTS.en),
        targetSystemName,
        minimumDeviceVersion
      ),
    [minimumDeviceVersion, parsedTitleTexts, targetSystemName]
  );

  const editorPreviewBody = useMemo(
    () =>
      applyPopupTemplateValues(
        resolvePreviewLocaleText(parsedBodyTexts, DEFAULT_BODY_TEXTS.en),
        targetSystemName,
        minimumDeviceVersion
      ),
    [minimumDeviceVersion, parsedBodyTexts, targetSystemName]
  );

  const handleAddPopup = () => {
    const draft = createDraftPopup();
    setSelectedPopupId(draft.id);
    setId(draft.id);
    setEnabled(draft.enabled);
    setTargetSystemName(draft.targetSystemName);
    setPopupType(draft.popupType);
    setMinimumDeviceVersion(draft.minimumDeviceVersion ?? '');
    setBlockerReleaseVersion(draft.blockerReleaseVersion ?? '');
    setDisplayPolicy(draft.displayPolicy);
    setTitleTextsJson(formatJson(draft.titleTexts));
    setBodyTextsJson(formatJson(draft.bodyTexts));
    setIsDraft(true);
    setStatusMessage(null);
    setShowEditorModal(true);
  };

  const handleEditPopup = (popup: PopupConfig) => {
    setSelectedPopupId(popup.id);
    setId(popup.id);
    setEnabled(popup.enabled);
    setTargetSystemName(popup.targetSystemName);
    setPopupType(popup.popupType);
    setMinimumDeviceVersion(popup.minimumDeviceVersion ?? '');
    setBlockerReleaseVersion(popup.blockerReleaseVersion ?? '');
    setDisplayPolicy(popup.displayPolicy);
    setTitleTextsJson(formatJson(popup.titleTexts));
    setBodyTextsJson(formatJson(popup.bodyTexts));
    setIsDraft(false);
    setStatusMessage(null);
    setShowEditorModal(true);
  };

  const handleCloseEditor = () => {
    setShowEditorModal(false);
  };

  const handleJsonFileUpload =
    (setValue: (value: string) => void, fieldName: string) =>
    async (event: ChangeEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const file = input.files?.[0];
      if (!file) {
        return;
      }

      try {
        const rawFile = await readJsonFile(file);
        const parsedMap = parseLocaleTextMap(rawFile, fieldName);
        setValue(formatJson(parsedMap));
        setStatusVariant('success');
        setStatusMessage(`${fieldName} loaded from ${file.name}.`);
      } catch (error) {
        setStatusVariant('danger');
        setStatusMessage(error instanceof Error ? error.message : `Failed to load ${fieldName}.`);
      }

      input.value = '';
    };

  const handleSave = async () => {
    try {
      const titleTexts = parseLocaleTextMap(titleTextsJson, 'Title LOC');
      const bodyTexts = parseLocaleTextMap(bodyTextsJson, 'Description LOC');
      const normalizedBlockerReleaseVersion = normalizeVersion(blockerReleaseVersion);
      const { key, name } = getPopupIdentity(targetSystemName, popupType);

      if (!minimumDeviceVersion.trim()) {
        throw new Error('Minimum device version is required.');
      }

      if (!normalizedBlockerReleaseVersion || normalizedBlockerReleaseVersion.length !== 11) {
        throw new Error('Version to block must use version format 003.013.000.');
      }

      const input = {
        id,
        key,
        name,
        enabled,
        place: 'MAIN_MENU',
        targetSystemName,
        popupType,
        minimumDeviceVersion: minimumDeviceVersion.trim(),
        blockerReleaseVersion: normalizedBlockerReleaseVersion,
        displayPolicy,
        titleTexts,
        bodyTexts,
      };

      if (isDraft) {
        await createPopupConfig({ variables: { input } });
      } else {
        await updatePopupConfig({ variables: { input } });
      }

      await refetch();
      setIsDraft(false);
      setShowEditorModal(false);
      setStatusVariant('success');
      setStatusMessage('Unsupported Pop Up saved.');
    } catch (error) {
      setStatusVariant('danger');
      setStatusMessage(error instanceof Error ? error.message : 'Something went wrong :(');
    }
  };

  const handleDelete = async (popupId: string) => {
    if (!window.confirm('Delete this unsupported popup config?')) {
      return;
    }

    try {
      await deletePopupConfig({ variables: { id: popupId } });
      await refetch();
      setStatusVariant('success');
      setStatusMessage('Unsupported Pop Up deleted.');

      if (selectedPopupId === popupId) {
        setSelectedPopupId(null);
      }
    } catch {
      setStatusVariant('danger');
      setStatusMessage('Failed to delete unsupported popup.');
    }
  };

  const isSaving = createLoading || updateLoading;

  return (
    <div className="d-flex flex-column">
      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <span className="font-weight-bold">
              <i className="fas fa-mobile-alt mr-2" />
              Unsupported Pop Up
            </span>
            {loading && <Spinner animation="border" size="sm" className="ml-2" />}
          </div>
          <Button variant="success" onClick={handleAddPopup}>
            <i className="fas fa-plus mr-2" />
            Add Pop Up
          </Button>
        </Card.Header>
        <Card.Body>
          {statusMessage ? (
            <Alert variant={statusVariant} className="py-2">
              {statusMessage}
            </Alert>
          ) : null}

          <Alert variant="info" className="py-2">
            Create one popup record per platform and popup type. Reminder records are shown before the blocker release.
            Blocker records are shown from the configured release onward. Use <code>{'{version}'}</code>,{' '}
            <code>{'{minimumDeviceVersion}'}</code>, and <code>{'{devicePlatform}'}</code> in localized copy.
          </Alert>

          <Table bordered size="sm" className="mb-4">
            <thead>
              <tr>
                <th>Platform</th>
                <th>Type</th>
                <th>Title Preview</th>
                <th>Description Preview</th>
                <th>Min Device Version</th>
                <th>Version To Block</th>
                <th>Frequency</th>
                <th>Status</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {unsupportedPopups.length ? (
                unsupportedPopups.map((popup) => (
                  <tr key={popup.id}>
                    <td>{popup.targetSystemName}</td>
                    <td>{popup.popupType}</td>
                    <td style={{ minWidth: 260 }}>
                      <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                        {formatJson(popup.titleTexts)}
                      </pre>
                    </td>
                    <td style={{ minWidth: 320 }}>
                      <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                        {formatJson(popup.bodyTexts)}
                      </pre>
                    </td>
                    <td>{popup.minimumDeviceVersion || '-'}</td>
                    <td>{popup.blockerReleaseVersion || '-'}</td>
                    <td>{popup.displayPolicy}</td>
                    <td>
                      <Badge variant={popup.enabled ? 'success' : 'secondary'}>
                        {popup.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </td>
                    <td>
                      <Button size="sm" className="mr-2" onClick={() => handleEditPopup(popup)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={deleteLoading}
                        onClick={() => handleDelete(popup.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center text-muted">
                    No unsupported popups configured yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      <Modal show={showEditorModal} onHide={handleCloseEditor} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{isDraft ? 'Add Unsupported Pop Up' : 'Edit Unsupported Pop Up'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mt-2">
            <Col xs={3}>Enabled</Col>
            <Col xs={9}>
              <Form.Check checked={enabled} onChange={(event) => setEnabled(event.currentTarget.checked)} />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Device platform</Col>
            <Col xs={6}>
              <Form.Control
                as="select"
                value={targetSystemName}
                onChange={(event) =>
                  setTargetSystemName(event.currentTarget.value === 'ANDROID' ? 'ANDROID' : 'IOS')
                }
              >
                <option value="IOS">iOS</option>
                <option value="ANDROID">Android</option>
              </Form.Control>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Popup type</Col>
            <Col xs={6}>
              <Form.Control
                as="select"
                value={popupType}
                onChange={(event) =>
                  setPopupType(event.currentTarget.value === 'BLOCKER' ? 'BLOCKER' : 'REMINDER')
                }
              >
                <option value="REMINDER">Reminder</option>
                <option value="BLOCKER">Blocker</option>
              </Form.Control>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Minimum device version</Col>
            <Col xs={6}>
              <Form.Control
                value={minimumDeviceVersion}
                onChange={(event) => setMinimumDeviceVersion(event.currentTarget.value)}
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Version to block</Col>
            <Col xs={6}>
              <Form.Control
                value={blockerReleaseVersion}
                onChange={(event) => setBlockerReleaseVersion(event.currentTarget.value)}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                  setBlockerReleaseVersion(normalizeVersion(event.currentTarget.value))
                }
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Display frequency</Col>
            <Col xs={6}>
              <Form.Control
                as="select"
                value={displayPolicy}
                disabled={popupType === 'BLOCKER'}
                onChange={(event) =>
                  setDisplayPolicy(event.currentTarget.value === 'EVERY_LAUNCH' ? 'EVERY_LAUNCH' : 'ONCE_PER_DAY')
                }
              >
                <option value="EVERY_LAUNCH">Every launch</option>
                <option value="ONCE_PER_DAY">Every 24 hours</option>
              </Form.Control>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Title LOC</Col>
            <Col xs={7}>
              <Form.Control
                as="textarea"
                rows={8}
                value={titleTextsJson}
                onChange={(event) => setTitleTextsJson(event.currentTarget.value)}
              />
            </Col>
            <Col xs={2}>
              <Form.File custom label="Browse" onChange={handleJsonFileUpload(setTitleTextsJson, 'Title LOC')} />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={3}>Description LOC</Col>
            <Col xs={7}>
              <Form.Control
                as="textarea"
                rows={12}
                value={bodyTextsJson}
                onChange={(event) => setBodyTextsJson(event.currentTarget.value)}
              />
            </Col>
            <Col xs={2}>
              <Form.File custom label="Browse" onChange={handleJsonFileUpload(setBodyTextsJson, 'Description LOC')} />
            </Col>
          </Row>

          <Row className="mt-4">
            <Col xs={3}>Live preview</Col>
            <Col xs={9}>
              <Card
                body
                style={{
                  backgroundColor: '#13253A',
                  borderRadius: 16,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#FFFFFF',
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 24,
                    lineHeight: 1.25,
                    textAlign: 'center',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {splitByHighlightTokens(editorPreviewTitle, previewHighlightTokens).map((part, index) => (
                    <span
                      key={`title-preview-${index}`}
                      style={part.type === 'highlight' ? { color: '#FF9A3D' } : undefined}
                    >
                      {part.value}
                    </span>
                  ))}
                </div>

                <div
                  style={{
                    marginTop: 16,
                    fontSize: 14,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {splitByHighlightTokens(editorPreviewBody, previewHighlightTokens).map((part, index) => (
                    <span
                      key={`body-preview-${index}`}
                      style={part.type === 'highlight' ? { color: '#FF9A3D', fontWeight: 700 } : undefined}
                    >
                      {part.value}
                    </span>
                  ))}
                </div>

                <div className="d-flex align-items-center justify-content-between mt-3">
                  <span>Don&apos;t show again.</span>
                  <Button size="sm" variant="warning">
                    How to Update
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditor}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSave}>
            {isSaving ? 'Saving...' : isDraft ? 'Create Pop Up' : 'Save Pop Up'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UnsupportedPopup;
